export type FridayStep = 'payroll' | 'bills' | 'unusual' | 'bank' | 'review' | 'done'
export type FridayPhase = 'idle' | 'asking' | 'review' | 'confirming' | 'done' | 'cancelled'

export type FridayAnswers = {
  payroll_cents: number | null
  bills_cents: number | null
  unusual_cents: number | null
  cash_on_hand_cents: number | null
}

export type FridayState = {
  phase: FridayPhase
  index: number
  bankStale: boolean
  steps: FridayStep[]
  answers: FridayAnswers
  cancelled: boolean
  parseError: boolean
}

export type FridayEvent =
  | { type: 'reset' }
  | { type: 'start'; bankStale?: boolean }
  | { type: 'answer'; text: string }
  | { type: 'cancel' }
  | { type: 'confirm' }
  | { type: 'confirmed' }
  | { type: 'fail' }

const NONE = /^(none|no|zero|0|n\/a|nothing)$/i

export function dollarsTextToCents(text: string): number | null {
  const t = String(text || '').trim()
  if (!t || NONE.test(t)) return 0
  const n = Number(t.replace(/[$,]/g, ''))
  if (!Number.isFinite(n)) return null
  return Math.round(n * 100)
}

export function fridaySteps(bankStale: boolean): FridayStep[] {
  const steps: FridayStep[] = ['payroll', 'bills', 'unusual']
  if (bankStale) steps.push('bank')
  steps.push('review')
  return steps
}

export function fridayPrompt(step: FridayStep): string {
  if (step === 'payroll') {
    return 'I already know the invoices and scheduled vendor bills. About how much will payroll be next week? Estimates are fine.'
  }
  if (step === 'bills') {
    return 'About how much will subcontractors, materials, and other bills be next week?'
  }
  if (step === 'unusual') {
    return 'Any unusual one-time expense? Say zero if there is none.'
  }
  if (step === 'bank') {
    return 'Bank balance looks stale. What is cash on hand today?'
  }
  return 'Check the inputs before sending them to the Cash Report.'
}

export function fridayProgress(index: number, total: number): string {
  return `Forecast check-in · question ${index + 1} of ${total}`
}

export function emptyFridayAnswers(): FridayAnswers {
  return { payroll_cents: null, bills_cents: null, unusual_cents: null, cash_on_hand_cents: null }
}

export function initialFridayState(): FridayState {
  return {
    phase: 'idle',
    index: -1,
    bankStale: false,
    steps: [],
    answers: emptyFridayAnswers(),
    cancelled: false,
    parseError: false,
  }
}

export function reduceFriday(state: FridayState, event: FridayEvent): FridayState {
  if (event.type === 'reset') return initialFridayState()
  if (event.type === 'start') {
    return {
      phase: 'asking',
      index: 0,
      bankStale: !!event.bankStale,
      steps: fridaySteps(!!event.bankStale),
      answers: emptyFridayAnswers(),
      cancelled: false,
      parseError: false,
    }
  }
  if (event.type === 'cancel') {
    return { ...state, phase: 'cancelled', cancelled: true, parseError: false }
  }
  if (event.type === 'answer') {
    if (state.phase !== 'asking') return state
    const step = state.steps[state.index]
    if (!step || step === 'review') return state
    const cents = dollarsTextToCents(event.text)
    if (cents == null) return { ...state, parseError: true }
    const answers = { ...state.answers }
    if (step === 'payroll') answers.payroll_cents = cents
    if (step === 'bills') answers.bills_cents = cents
    if (step === 'unusual') answers.unusual_cents = cents
    if (step === 'bank') answers.cash_on_hand_cents = cents
    const index = state.index + 1
    const nextStep = state.steps[index]
    return {
      ...state,
      answers,
      index,
      parseError: false,
      phase: nextStep === 'review' ? 'review' : 'asking',
    }
  }
  if (event.type === 'confirm') {
    if (state.phase !== 'review' || state.cancelled) return state
    return { ...state, phase: 'confirming' }
  }
  if (event.type === 'confirmed') {
    if (state.phase !== 'confirming') return state
    return { ...state, phase: 'done' }
  }
  if (event.type === 'fail') {
    if (state.phase !== 'confirming') return state
    return { ...state, phase: 'review' }
  }
  return state
}

export function fridayAskCopy(state: FridayState): string {
  const step = state.steps[state.index] || 'review'
  const qCount = Math.max(state.steps.length - 1, 1)
  if (step === 'review') return fridayPrompt('review')
  return `${fridayProgress(state.index, qCount)}\n${fridayPrompt(step)}`
}

export async function confirmFridayCheckIn(
  state: FridayState,
  deps: {
    save: (body: FridayAnswers & { effective_date?: string }) => Promise<{ data?: { version?: { id: string }; actions?: unknown } }>
    writeVersion: (version: unknown, extras?: { actions?: unknown }) => void
    dateKey: () => string
  },
): Promise<{ state: FridayState; version?: { id: string } }> {
  if (state.cancelled || state.phase !== 'confirming') return { state }
  const saved = await deps.save({
    ...state.answers,
    effective_date: deps.dateKey(),
  })
  const version = saved.data?.version
  if (!version) throw new Error('Forecast version missing after Friday confirm')
  deps.writeVersion(version, { actions: saved.data?.actions })
  return { state: reduceFriday(state, { type: 'confirmed' }), version }
}
